import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyUsersQuery, useInviteCompanyUserMutation } from './hooks';
import { Button, Notice } from './Chrome';
import { errorMessage } from './errors';

/**
 * The invitation dialog. Self-contained — it reads the seat count itself — so it can be
 * opened from any screen, not only the one that lists the people.
 */
export default function Invite({
  groupId,
  onClose,
}: {
  groupId: string;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const { data } = useCompanyUsersQuery(groupId, { limit: 1 });
  const invite = useInviteCompanyUserMutation();

  const seats = data?.seats;
  const noSeats = !!seats && seats.available !== null && seats.available < 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10,56,51,.35)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="leadhr-invite-title"
    >
      <form
        className="flex w-full max-w-md flex-col gap-4 rounded-xl p-6"
        style={{ background: 'var(--lh-card)' }}
        onSubmit={(event) => {
          event.preventDefault();
          if (noSeats) {
            return;
          }
          invite.mutate({ groupId, payload: { email, name } }, { onSuccess: onClose });
        }}
      >
        <h2 id="leadhr-invite-title" className="lh-h3 m-0">
          {t('com_ui_leadhr_invite_title')}
        </h2>

        {seats ? (
          <p className="lh-sub m-0">
            {seats.available === null
              ? t('com_ui_leadhr_invite_no_limit')
              : t('com_ui_leadhr_invite_seats_left', { count: seats.available })}
          </p>
        ) : null}

        <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
          {t('com_ui_leadhr_invite_email')}
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="lh-input font-normal"
          />
        </label>

        <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
          {t('com_ui_leadhr_invite_name')}
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="lh-input font-normal"
          />
        </label>

        {invite.isError ? (
          <Notice>{errorMessage(invite.error, t('com_ui_leadhr_action_failed'))}</Notice>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <Button onClick={onClose} disabled={invite.isLoading}>
            {t('com_ui_leadhr_cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={invite.isLoading || noSeats || !email.trim()}
          >
            {t('com_ui_leadhr_invite_send')}
          </Button>
        </div>
      </form>
    </div>
  );
}
